import { ListItemIcon, ListItemText, MenuItem, MenuItemProps, Tooltip } from "@mui/material";
import { MessageSeverity } from "@sinch/core/entities/serverEnums";
import { useOpenState } from "@sinch/hooks/utilities/useOpenState";
import { MdsDelete } from "@sinch/icons";
import { parseMutationErrorsFromData } from "@sinch/utils/apollo";
import { useSnackbar } from "notistack";
import { propEq } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRatelist } from "@sinch/components/application/Rate/useRatelist";
import { ConditionalWrapper } from "@sinch/components/ConditionalWrapper/ConditionalWrapper";
import { DeleteConfirmDialog } from "@sinch/components/DeleteConfirmDialog/DeleteConfirmDialog";

export const DeleteMenuItem = ({
  id,
  onPerformedAction,
  ...props
}: MenuItemProps & { id: string; onPerformedAction: () => void }) => {
  const [openDialog, open, close] = useOpenState();
  const { t } = useTranslation(["common", "draft"]);

  const { enqueueSnackbar } = useSnackbar();

  const { remove: deleteRatelist } = useRatelist();

  const handleConfirmDelete = useCallback(async () => {
    try {
      const response = await deleteRatelist({ variables: { id: id } });
      close();
      if (response.data?.deleteRatelist.result) {
        enqueueSnackbar(t("deleteSuccessfully"), { variant: "success" });
        onPerformedAction();
      } else {
        const firstMessage = parseMutationErrorsFromData(response).find((message) =>
          propEq("severity", MessageSeverity.Error)(message)
        )?.message;
        if (isNotNilOrEmpty(firstMessage)) {
          enqueueSnackbar(firstMessage, { variant: "error" });
        } else {
          enqueueSnackbar(t("ratelistError"), { variant: "error" });
        }
      }
    } catch {
      enqueueSnackbar(t("ratelistError"), { variant: "error" });
    }
  }, [deleteRatelist, id, close, onPerformedAction, enqueueSnackbar, t]);

  return (
    <>
      <ConditionalWrapper
        condition={!!props.disabled}
        wrapper={(children) => (
          <Tooltip title={t("RateBuilder.deletingDisabledDueBecauseUsed")}>
            <div>{children}</div>
          </Tooltip>
        )}
      >
        <MenuItem color="error" onClick={open} {...props}>
          <ListItemIcon>
            <MdsIcon color="error" icon={MdsDelete} />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>{t("delete")}</ListItemText>
        </MenuItem>
      </ConditionalWrapper>
      {openDialog && (
        <DeleteConfirmDialog maxWidth="sm" onCancel={close} onConfirm={handleConfirmDelete} open={openDialog} />
      )}
    </>
  );
};
